import { Link } from "react-router-dom";
import { ArrowRight, Building2, Compass, Handshake, Leaf } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useWhatsApp, WhatsAppIcon } from "@/components/Layout";

const ABOUT_IMAGE = "https://images.pexels.com/photos/15391694/pexels-photo-15391694.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DETAIL_IMAGE =
  "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBhcmNoaXRlY3R1cmFsJTIwaG91c2UlMjBleHRlcmlvciUyMG1pbmltYWxpc218ZW58MHx8fHwxNzkyNjAyNDEwfDA&ixlib=rb-4.1.0&q=85";

const PILARES = [
  { icon: Building2, title: "Qualidade construtiva", text: "Cada empreendimento é conduzido com rigor técnico, do primeiro traço à entrega das chaves." },
  { icon: Compass, title: "Funcionalidade", text: "Plantas pensadas para a vida real: circulação fluida, ambientes integrados e bom aproveitamento dos espaços." },
  { icon: Leaf, title: "Conforto e bem-estar", text: "Iluminação natural, ventilação e acabamentos que valorizam o dia a dia de quem mora." },
  { icon: Handshake, title: "Transparência", text: "Relação direta e clara com cada cliente, do primeiro contato à escritura." },
];

export default function Sobre() {
  const openWa = useWhatsApp();
  return (
    <div className="pt-[72px]" data-testid="about-page">
      <Seo
        title="Sobre a Casa Vale | Engenharia e Construção"
        description="A Casa Vale Engenharia e Construção desenvolve imóveis que unem funcionalidade, conforto e atenção aos detalhes. Conheça a construtora."
      />

      <section className="relative bg-slate-900 py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={DETAIL_IMAGE} alt="" aria-hidden="true" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 to-slate-900" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-limebrand text-xs font-bold uppercase tracking-[0.3em] mb-4">A Construtora</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight max-w-3xl leading-[1.08]" data-testid="about-title">
            Construindo espaços para viver bem.
          </h1>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal>
            <div className="clipped-frame overflow-hidden">
              <img src="/empreendimentos-casavale.png" alt="Detalhe arquitetônico de empreendimento da Casa Vale" className="w-full aspect-[4/5] object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="text-brand text-xs font-bold uppercase tracking-[0.3em] mb-3">Quem somos</p>
              <h2 className="font-serif-display text-3xl sm:text-4xl text-slate-900 tracking-tight leading-tight" data-testid="about-heading">
                Casa Vale Engenharia e Construção
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed" data-testid="about-text">
                A Casa Vale Engenharia e Construção desenvolve imóveis que unem funcionalidade, conforto e atenção aos
                detalhes. Nosso compromisso é oferecer espaços pensados para diferentes momentos da vida, valorizando a
                qualidade construtiva e o bem-estar de quem escolhe viver em nossos empreendimentos.
              </p>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Atuamos na construção de casas e apartamentos prontos para morar, cuidando de cada etapa — do projeto ao
                acabamento — para que a escolha do seu novo lar seja simples, segura e transparente.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/imoveis"
                  className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold px-7 py-3.5 rounded-full transition-colors duration-200"
                  data-testid="about-cta-imoveis"
                >
                  Conhecer Imóveis <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  type="button"
                  onClick={() => openWa("Olá! Vim pelo site da Casa Vale e gostaria de conversar com a equipe.")}
                  className="inline-flex items-center gap-2 border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold px-7 py-3 rounded-full transition-colors duration-200"
                  data-testid="about-cta-whatsapp"
                >
                  <WhatsAppIcon className="w-4 h-4" /> Falar com a equipe
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#FAFAFA]" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-brand text-xs font-bold uppercase tracking-[0.3em] mb-3 text-center">Nosso compromisso</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-slate-900 tracking-tight text-center mb-14" data-testid="pillars-heading">
              O que guia cada empreendimento
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILARES.map((pilar, i) => (
              <Reveal key={pilar.title} delay={i * 0.1}>
                <div className="bg-white border border-slate-200 rounded-2xl p-7 h-full hover:shadow-lg transition-shadow duration-300">
                  <span className="w-11 h-11 rounded-xl bg-brand-light flex items-center justify-center">
                    <pilar.icon className="w-5 h-5 text-brand" />
                  </span>
                  <h3 className="mt-5 text-base font-bold text-slate-900">{pilar.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{pilar.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
